import { z } from 'zod';
import { restricted } from './redaction.js';
/**
 * Authentication contracts (slice-00 §7, amendment A2).
 *
 * Shared verbatim by apps/api, apps/web, and apps/field: the API validates
 * inbound requests with these, and the web forms validate with the same objects
 * so a rule cannot drift between the two.
 */
/**
 * The JWT `aud` claim, and the hard boundary between the three apps (§4). Not a
 * role — a FAMILY token is rejected by the staff API before any permission
 * check runs.
 */
export const audienceSchema = z.enum(['staff', 'caregiver', 'family']);
/** Password policy per spec §10.1: 12 characters minimum, no forced rotation. */
export const PASSWORD_MIN_LENGTH = 12;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
    .max(256, 'Passwords longer than 256 characters are not accepted.');
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email('Enter a valid email address.')
    .max(320);
// ---------------------------------------------------------------------------
// POST /v1/auth/login
// ---------------------------------------------------------------------------
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Enter your password.').max(256),
    /**
     * Which app is asking. Client-supplied, and safe to be: it only ever narrows
     * the candidate set, and it is matched against `user.audience` in the
     * database. A caregiver claiming `staff` matches no candidate row and gets
     * the same generic failure as a wrong password — the claim cannot promote
     * anyone, only exclude them.
     */
    audience: audienceSchema.default('staff'),
    /**
     * Optional fast path from `/login/:agencySlug` (§7). Pre-filters candidates
     * server-side. Convenience only — never required, never trusted for
     * authorization, and a wrong slug produces the same generic failure.
     */
    agencySlug: z.string().trim().min(1).max(64).optional(),
    /**
     * TOTP code, required once an authenticator is enrolled.
     *
     * Not in §7's request sketch, which stops at email and password. Added
     * because the alternative is an enrolled second factor that is never
     * demanded — MFA that exists in the database and nowhere in the login path
     * is not MFA. Only ever consulted after the password has verified, so its
     * presence discloses nothing.
     */
    mfaCode: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.')
        .optional(),
});
export const agencySummarySchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
});
export const authenticatedUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    audience: audienceSchema,
    /**
     * §8's field-redaction demonstrator: stripped entirely unless the caller
     * holds `admin:users`.
     *
     * Marked here, on the field, rather than in a per-route map — so it is
     * redacted everywhere this schema appears, including nested inside
     * `meResponseSchema` and inside any array of users a later slice adds. See
     * redaction.ts for why that distinction is the whole point.
     */
    phone: restricted(z.string().nullable().optional(), 'admin:users'),
    mfaEnrolled: z.boolean(),
});
export const tokenPairSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Seconds until the access token expires. Lets a client refresh proactively. */
    expiresIn: z.number().int().positive(),
    tokenType: z.literal('Bearer'),
});
/** One match: tokens. */
export const loginSuccessSchema = tokenPairSchema.extend({
    outcome: z.literal('authenticated'),
    user: authenticatedUserSchema,
    agency: agencySummarySchema,
});
/**
 * Two or more matches: a selection token and the agencies it is bound to.
 *
 * The selection token is NOT an access token and grants nothing. It is single
 * use, expires in 60 seconds, and is bound to the exact candidate set whose
 * passwords were just verified — so it cannot be replayed, and cannot be
 * redirected at an agency that was not in that set.
 */
export const loginSelectionRequiredSchema = z.object({
    outcome: z.literal('agency_selection_required'),
    selectionToken: z.string(),
    expiresIn: z.number().int().positive(),
    agencies: z.array(agencySummarySchema).min(2),
});
export const loginResponseSchema = z.discriminatedUnion('outcome', [
    loginSuccessSchema,
    loginSelectionRequiredSchema,
]);
// ---------------------------------------------------------------------------
// POST /v1/auth/login/select
// ---------------------------------------------------------------------------
export const loginSelectRequestSchema = z.object({
    selectionToken: z.string().min(1),
    agencyId: z.string().uuid(),
    /**
     * TOTP code, when the chosen agency's account has an authenticator.
     *
     * Without it the picker was a way round the second factor: the password
     * verified, an agency was picked, and a session was issued. The API asks for
     * it with AUTH_MFA_CODE_REQUIRED and keeps the selection while it does.
     */
    mfaCode: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.')
        .optional(),
});
// ---------------------------------------------------------------------------
// POST /v1/auth/refresh · POST /v1/auth/logout
// ---------------------------------------------------------------------------
export const refreshRequestSchema = z.object({
    refreshToken: z.string().min(1),
});
export const refreshResponseSchema = tokenPairSchema;
export const logoutRequestSchema = z.object({
    /** Optional: logout works from the access token alone. */
    refreshToken: z.string().min(1).optional(),
});
// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------
export const passwordForgotRequestSchema = z.object({
    email: emailSchema,
    audience: audienceSchema.default('staff'),
});
export const passwordResetRequestSchema = z.object({
    token: z.string().min(1),
    password: passwordSchema,
});
/**
 * Both password endpoints return this same acknowledgement whether or not the
 * address exists. Anything conditional here is an enumeration oracle, exactly
 * as it would be on login.
 */
export const acknowledgementSchema = z.object({ acknowledged: z.literal(true) });
// ---------------------------------------------------------------------------
// MFA
// ---------------------------------------------------------------------------
export const mfaEnrollResponseSchema = z.object({
    /** Base32 TOTP secret, shown once. */
    secret: z.string(),
    /** otpauth:// URI for a QR code. */
    uri: z.string(),
});
export const mfaVerifyRequestSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.'),
});
export const mfaVerifyResponseSchema = z.object({ enrolled: z.literal(true) });
// ---------------------------------------------------------------------------
// GET /v1/auth/me
// ---------------------------------------------------------------------------
export const meResponseSchema = z.object({
    user: authenticatedUserSchema,
    agency: agencySummarySchema,
    /**
     * The agency's IANA zone. A client works out "today" with `agencyLocalDay`
     * from this, never from the browser's own zone or a UTC slice (R-1e): from
     * 7pm in New York the browser's UTC day is tomorrow.
     */
    timeZone: z.string(),
    roles: z.array(z.string()),
    /**
     * Resolved from the caller's roles at request time, never read from the
     * token — embedded permissions go stale the moment a role changes (§7).
     */
    permissions: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// GET /v1/health
// ---------------------------------------------------------------------------
export const healthResponseSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    checks: z.record(z.enum(['ok', 'failing'])),
});
//# sourceMappingURL=auth.js.map